import { NavLink, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  LayoutDashboard,
  Package,
  Film,
  Settings as SettingsIcon,
  Plus,
} from "lucide-react";
import { motion as motionTokens } from "@isale/design-tokens";
import { useTranslation } from "@/shared/i18n/I18nProvider";
import { useTheme } from "@/shared/theme/ThemeProvider";
import { cn } from "@/lib/utils";

interface MobileBottomTabProps {
  /**
   * When true, slide the bar off-screen (translateY 100%) and remove it from
   * the a11y tree. Used by Layout on depth-2 mobile screens — the back link
   * in content takes over the "where am I" job.
   */
  hidden?: boolean;
}

/**
 * Mobile-viewport bottom tab navigation (web responsive, < md:768px).
 *
 * Mirrors the RN app's `AppTabBar` cradle (design v2 — approved 2026-06-07):
 * 2 tabs · center raised FAB nested in a curved "cradle" · 2 tabs, with a pink
 * accent pill under the FAB. The bar surface is an SVG <path> whose top edge
 * bulges UP to cradle the round FAB (CSS can't draw a curved bar top edge).
 * Light reads via a drop-shadow + themed hairline; dark via a white RIM-LIGHT
 * highlight (a dark shadow/border can't read on a dark-on-dark bar).
 *
 * The center FAB is CONTEXT-AWARE (parity with the RN AppTabBar): on the
 * Products tab it dispatches `isale:new-product` (Products opens its create
 * modal); on every other tab it dispatches `isale:new-clip` (the global
 * NewClipModal in App.tsx opens in place). One FAB replaces the old per-screen
 * floating FABs — no duplicate "create" button on mobile.
 * Theme-aware via useTheme().resolved + --color-* CSS vars (ADR-011). The 4
 * tabs mirror the RN TabsLayout; labels flow through tabs.* i18n keys.
 *
 * Visibility: always rendered so the translateY transition has a stable origin —
 * hidden=true slides it off-screen + yanks it from the a11y tree without
 * unmounting (no entrance pop on return).
 */

// Cradle geometry in SVG viewBox units; preserveAspectRatio="none" scales it to
// the bar width. Mirrors AppTabBar.tsx: two mirrored cubic béziers with
// horizontal tangents at the flat ends + the peak so the bump never overshoots.
const RAISE = 30; // how far the FAB floats above the flat bar top
const BAR_H = 58; // visible bar (tab row) height
const VB_W = 390;
const VB_H = RAISE + BAR_H; // 88
const FAB = 56;
const HUMP_HALF = 62;
const BUMP_RISE = 16;
/** FAB peek above the flat bar when lowered — a tactile cue, not a
 *  content-covering bulge. With the down-scale the FAB top sits BELOW the
 *  content edge so nothing protrudes. Parity with the RN AppTabBar
 *  (Vincent 2026-06-10). */
const FAB_PEEK = 2;
const FAB_DOWN_SCALE = 0.78;
const CX = VB_W / 2;
const TOP_FLAT = RAISE;
/** Cradle top edge for a given centre-bump height. bump=0 → FLAT bar top
 *  (nothing bulges over content); bump=BUMP_RISE → the raised cradle. */
function topEdge(bump: number): string {
  const peak = TOP_FLAT - bump;
  return (
    `M0 ${TOP_FLAT} H${CX - HUMP_HALF} ` +
    `C${CX - HUMP_HALF * 0.5} ${TOP_FLAT} ${CX - HUMP_HALF * 0.5} ${peak} ${CX} ${peak} ` +
    `C${CX + HUMP_HALF * 0.5} ${peak} ${CX + HUMP_HALF * 0.5} ${TOP_FLAT} ${CX + HUMP_HALF} ${TOP_FLAT} ` +
    `H${VB_W}`
  );
}
// flat ↔ raised endpoints; framer-motion interpolates `d` (same command
// structure, only the peak Y differs).
const EDGE_UP = topEdge(BUMP_RISE);
const EDGE_FLAT = topEdge(0);
const FILL_UP = `${EDGE_UP} V${VB_H} H0 Z`;
const FILL_FLAT = `${EDGE_FLAT} V${VB_H} H0 Z`;

interface Tab {
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
}
const LEFT_TABS: Tab[] = [
  { to: "/dashboard", labelKey: "tabs.home", icon: LayoutDashboard },
  { to: "/products", labelKey: "tabs.products", icon: Package },
];
const RIGHT_TABS: Tab[] = [
  { to: "/projects", labelKey: "tabs.clips", icon: Film },
  { to: "/settings", labelKey: "tabs.account", icon: SettingsIcon },
];

export function MobileBottomTab({ hidden = false }: MobileBottomTabProps) {
  const { t } = useTranslation();
  const { resolved } = useTheme();
  const location = useLocation();
  // Context-aware FAB (parity with mobile-native AppTabBar): on the Products
  // tab the center FAB creates a PRODUCT; on every other tab it creates a CLIP.
  // Each route screen listens on its own window event (Products →
  // isale:new-product; the global NewClipModal → isale:new-clip), so this
  // single FAB replaces the old per-screen floating FABs.
  const isProductsTab = location.pathname.startsWith("/products");
  const fabEvent = isProductsTab ? "isale:new-product" : "isale:new-clip";
  const fabLabel = isProductsTab
    ? t("productsWeb.createCta")
    : t("projectsWeb.newModalTitle");

  // FAB raised on the create-relevant roots (dashboard/products/projects),
  // lowered on /settings ("Tôi") — parity with the RN AppTabBar. On
  // mobile-web the bar auto-hides on depth-2 (Layout `hidden`), so only the 4
  // roots reach here. framer-motion spring matches the native withSpring feel.
  const prefersReduced = useReducedMotion();
  const fabUp =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/products") ||
    location.pathname.startsWith("/projects");
  const fabTransition = prefersReduced
    ? { duration: 0 }
    : ({
        type: "spring",
        stiffness: motionTokens.spring.rn.stiffness,
        damping: motionTokens.spring.rn.damping,
      } as const);

  // Cradle edge cue: light = drop-shadow + themed hairline; dark = white
  // rim-light (a dark shadow/border can't read on a dark-on-dark bar).
  const cradleStroke =
    resolved === "dark" ? "rgba(255,255,255,0.2)" : "var(--color-border)"; // palette-ok: fixed cradle edge scrim · style-ok: theme-conditional cradle stroke
  const cradleStrokeWidth = resolved === "dark" ? 1.5 : 1;
  const cradleFilter =
    resolved === "dark"
      ? undefined
      : "drop-shadow(0 -3px 8px rgba(15,23,42,0.08))"; // palette-ok: fixed light cradle shadow

  const renderTab = (tab: Tab) => (
    <NavLink
      key={tab.to}
      to={tab.to}
      end={tab.to === "/settings"}
      tabIndex={hidden ? -1 : 0}
      className={({ isActive }) =>
        cn(
          "flex flex-col items-center justify-center gap-1 px-2 text-3xs font-bold",
          isActive ? "text-primary" : "text-fg-subtle",
        )
      }
    >
      <tab.icon className="h-5 w-5" />
      <span>{t(tab.labelKey)}</span>
    </NavLink>
  );

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-20 transition-transform duration-200 ease-out md:hidden"
      style={{
        height: `calc(${VB_H}px + env(safe-area-inset-bottom))`,
        transform: hidden ? "translateY(100%)" : "translateY(0)",
        pointerEvents: hidden ? "none" : "auto",
      }}
      aria-label="Bottom navigation"
      aria-hidden={hidden}
    >
      {/* Cradle bar surface — elevated fill + curved top edge cue. */}
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        style={{ filter: cradleFilter }}
        aria-hidden="true"
      >
        <motion.path
          initial={false}
          animate={{ d: fabUp ? FILL_UP : FILL_FLAT }}
          transition={fabTransition}
          className="fill-elevated"
        />
        <motion.path
          initial={false}
          animate={{ d: fabUp ? EDGE_UP : EDGE_FLAT }}
          transition={fabTransition}
          fill="none"
          stroke={cradleStroke}
          strokeWidth={cradleStrokeWidth}
        />
      </svg>

      {/* Tab row (2 | FAB gap | 2) pinned to the bottom. */}
      <div
        className="absolute inset-x-0 bottom-0 grid"
        style={{
          gridTemplateColumns: "1fr 1fr 76px 1fr 1fr",
          height: BAR_H,
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {LEFT_TABS.map(renderTab)}
        <div />
        {RIGHT_TABS.map(renderTab)}
      </div>

      {/* Center raised FAB nested in the cradle bump. framer-motion springs
          the y between raised/lowered per route (Vincent 2026-06-10); x stays
          -50% to keep it centered on the left:50% anchor. */}
      <motion.button
        type="button"
        onClick={() => window.dispatchEvent(new Event(fabEvent))}
        aria-label={fabLabel}
        tabIndex={hidden ? -1 : 0}
        className="absolute left-1/2 top-0 flex items-center justify-center rounded-full border-4 border-canvas text-white"
        initial={false}
        animate={{ x: "-50%", y: fabUp ? 0 : RAISE - FAB_PEEK, scale: fabUp ? 1 : FAB_DOWN_SCALE }}
        transition={fabTransition}
        whileTap={prefersReduced ? undefined : { scale: 0.95 }}
        style={{
          width: FAB,
          height: FAB,
          // style-ok: gradient
          background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-hover))", // style-ok: gradient
          // style-ok: boxShadow color
          boxShadow: "0 8px 16px -4px var(--color-primary-hover)",
        }}
      >
        <Plus className="h-6 w-6" strokeWidth={2.5} />
      </motion.button>

      {/* Pink accent pill — belongs to the raised cradle; fades + drops with
          the FAB so it only shows when raised. */}
      <motion.div
        aria-hidden="true"
        className="absolute left-1/2 bg-accent"
        initial={false}
        animate={{ x: "-50%", y: fabUp ? 0 : RAISE - FAB_PEEK, opacity: fabUp ? 1 : 0 }}
        transition={fabTransition}
        style={{
          top: FAB + 6,
          width: 34,
          height: 5,
          borderRadius: 999,
        }}
      />
    </nav>
  );
}
